import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, Sparkles, X, Sailboat, Wine, Heart, Map, Utensils, Bike, MoreHorizontal, Wand2, Loader2 } from 'lucide-react';
import { adminApi, publicApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Textarea, Select, ImageUpload } from './AdminUI';

const EMPTY = {
  slug: '', city_slug: 'desenzano', title: '', subtitle: '', description: '',
  image: '', experience_type: '', price: '', currency: 'EUR', duration: '',
  includes: [], highlights: [], max_people: '',
  languages: ['it', 'en'],
  booking_url: '', booking_email: '', booking_phone: '',
  featured: false, published: true, order: 0
};

const LANG_CHOICES = ['it', 'en', 'de', 'fr', 'es', 'zh', 'ar'];

const TYPE_CHOICES = [
  { key: 'barca', label: 'In barca', icon: Sailboat, color: 'bg-sky-100 text-sky-800' },
  { key: 'cantina', label: 'In cantina', icon: Wine, color: 'bg-purple-100 text-purple-800' },
  { key: 'wellness', label: 'Wellness & SPA', icon: Heart, color: 'bg-pink-100 text-pink-800' },
  { key: 'tour', label: 'Tour & Guide', icon: Map, color: 'bg-emerald-100 text-emerald-800' },
  { key: 'gourmet', label: 'Gourmet', icon: Utensils, color: 'bg-orange-100 text-orange-800' },
  { key: 'sport', label: 'Sport & Adventure', icon: Bike, color: 'bg-amber-100 text-amber-800' },
  { key: 'other', label: 'Altro', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-800' }
];

function ChipList({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const items = value || [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder}
               onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm">+</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-900 text-xs">
            {it}
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="hover:text-red-600"><X size={12}/></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AdminExperiences() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => adminApi.experiences().then(setItems);
  useEffect(() => { load(); publicApi.cities().then(setCities); }, []);

  const classifyWithAI = async () => {
    if (!editing.title) { alert('Inserisci prima il titolo (e possibilmente la descrizione).'); return; }
    setAiBusy(true);
    try {
      const { experience_type, highlights } = await adminApi.classifyExperience({
        title: editing.title,
        description: editing.description || ''
      });
      const currentHighlights = editing.highlights || [];
      const merged = [...currentHighlights];
      highlights.forEach(h => { if (!merged.includes(h)) merged.push(h); });
      setEditing({ ...editing, experience_type, highlights: merged });
    } catch (e) {
      alert('Errore AI: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.slug || !editing.title || !editing.city_slug) { alert('Slug, titolo e città obbligatori'); return; }
    const payload = {
      slug: editing.slug,
      city_slug: editing.city_slug,
      title: editing.title,
      subtitle: editing.subtitle || null,
      description: editing.description || null,
      image: editing.image || null,
      experience_type: editing.experience_type || null,
      price: editing.price === '' || editing.price == null ? null : Number(editing.price),
      currency: editing.currency || 'EUR',
      duration: editing.duration || null,
      includes: editing.includes || [],
      highlights: editing.highlights || [],
      max_people: editing.max_people === '' || editing.max_people == null ? null : Number(editing.max_people),
      languages: editing.languages || [],
      booking_url: editing.booking_url || null,
      booking_email: editing.booking_email || null,
      booking_phone: editing.booking_phone || null,
      featured: !!editing.featured,
      published: !!editing.published,
      order: Number(editing.order) || 0
    };
    try {
      if (editing.id) await adminApi.updateExperience(editing.id, payload);
      else await adminApi.createExperience(payload);
      setEditing(null); load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare "${row.title}"?`)) return;
    await adminApi.deleteExperience(row.id); load();
  };

  const toggleLang = (l) => {
    const set = new Set(editing.languages || []);
    set.has(l) ? set.delete(l) : set.add(l);
    setEditing({ ...editing, languages: Array.from(set) });
  };

  return (
    <div>
      <PageHeader
        title="YUP Experiences"
        subtitle={`${items.length} experiences configurate — pacchetti venduti da YUP per città`}
        action={<button onClick={() => setEditing({...EMPTY})} className="btn-primary flex items-center gap-2" data-testid="admin-experience-new-btn"><Plus size={16}/> Nuova experience</button>}
      />

      <Table
        columns={[
          { key: 'title', label: 'Titolo', render: r => <div><div className="font-semibold">{r.title}</div><div className="text-xs text-gray-500">/{r.city_slug}/experiences/{r.slug}</div></div> },
          { key: 'city_slug', label: 'Città' },
          { key: 'experience_type', label: 'Tipo', render: r => {
            const ty = TYPE_CHOICES.find(t => t.key === r.experience_type);
            if (!ty) return '—';
            const Icon = ty.icon;
            return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ty.color}`}><Icon size={12}/> {ty.label}</span>;
          }},
          { key: 'price', label: 'Prezzo', render: r => typeof r.price === 'number' ? `€ ${r.price.toFixed(0)}` : '—' },
          { key: 'duration', label: 'Durata', render: r => r.duration || '—' },
          { key: 'featured', label: 'Top', render: r => r.featured ? <span className="text-amber-700 text-xs font-bold">★ Top</span> : '—' },
          { key: 'published', label: 'Stato', render: r => r.published ? <span className="text-green-700 text-xs font-semibold">Pubblicata</span> : <span className="text-gray-500 text-xs">Bozza</span> }
        ]}
        rows={items}
        actions={row => (
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        )}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica experience' : 'Nuova experience'} maxWidth="max-w-3xl">
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Città *">
                <Select value={editing.city_slug} onChange={e => setEditing({...editing, city_slug: e.target.value})}>
                  {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Slug (URL) *">
                <Input required value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} placeholder="gita-barca-degustazione"/>
              </Field>
            </div>
            <Field label="Titolo *"><Input required value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} placeholder="Es. Gita in barca + degustazione in cantina"/></Field>
            <Field label="Sottotitolo"><Input value={editing.subtitle || ''} onChange={e => setEditing({...editing, subtitle: e.target.value})} placeholder="Un pomeriggio tra lago e vigneti..."/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo experience">
                <Select value={editing.experience_type || ''} onChange={e => setEditing({...editing, experience_type: e.target.value})} data-testid="experience-type-select">
                  <option value="">(nessuno)</option>
                  {TYPE_CHOICES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </Select>
              </Field>
              <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})}/></Field>
            </div>
            <Field label="Immagine hero"><ImageUpload value={editing.image} onChange={v => setEditing({...editing, image: v})} hint="Foto principale mostrata nella card e nella pagina detail"/></Field>
            <Field label="Descrizione dettagliata">
              <Textarea rows={6} value={editing.description || ''} onChange={e => setEditing({...editing, description: e.target.value})} placeholder="Racconta l'experience: cosa succede, dove si parte, atmosfera..."/>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={classifyWithAI}
                  disabled={aiBusy || !editing.title}
                  data-testid="experience-ai-classify-btn"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="L'AI suggerisce tipo experience + highlights dalla descrizione"
                >
                  {aiBusy ? <><Loader2 size={13} className="animate-spin"/> Analisi...</> : <><Wand2 size={13}/> Suggerisci con AI</>}
                </button>
                <span className="ml-2 text-xs text-gray-500">Compila titolo e descrizione, poi lascia che l'AI classifichi e generi gli highlights.</span>
              </div>
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Prezzo (€)"><Input type="number" step="1" value={editing.price} onChange={e => setEditing({...editing, price: e.target.value})} placeholder="89"/></Field>
              <Field label="Durata"><Input value={editing.duration || ''} onChange={e => setEditing({...editing, duration: e.target.value})} placeholder="3 ore"/></Field>
              <Field label="Max persone"><Input type="number" value={editing.max_people ?? ''} onChange={e => setEditing({...editing, max_people: e.target.value})} placeholder="12"/></Field>
            </div>

            <Field label="Lingue disponibili">
              <div className="flex flex-wrap gap-2">
                {LANG_CHOICES.map(l => (
                  <button key={l} type="button" onClick={() => toggleLang(l)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${(editing.languages || []).includes(l) ? 'bg-[color:var(--yup-primary)] text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-4 mt-2">
              <Field label="Highlights (breve, uno per riga)">
                <ChipList value={editing.highlights || []} onChange={v => setEditing({...editing, highlights: v})} placeholder="Aperitivo al tramonto sul molo"/>
              </Field>
              <Field label="Cosa include">
                <ChipList value={editing.includes || []} onChange={v => setEditing({...editing, includes: v})} placeholder="Skipper + 3 calici + tagliere"/>
              </Field>
            </div>

            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="font-semibold text-emerald-900 mb-2 text-sm">Prenotazione</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="URL prenotazione"><Input value={editing.booking_url || ''} onChange={e => setEditing({...editing, booking_url: e.target.value})} placeholder="https://..."/></Field>
                <Field label="Email"><Input type="email" value={editing.booking_email || ''} onChange={e => setEditing({...editing, booking_email: e.target.value})}/></Field>
                <Field label="Telefono"><Input value={editing.booking_phone || ''} onChange={e => setEditing({...editing, booking_phone: e.target.value})} placeholder="+39 ..."/></Field>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Top pick">
                <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={!!editing.featured} onChange={e => setEditing({...editing, featured: e.target.checked})}/> Featured (badge oro)</label>
              </Field>
              <Field label="Stato">
                <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={!!editing.published} onChange={e => setEditing({...editing, published: e.target.checked})}/> Pubblicata</label>
              </Field>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
